import * as React from 'react';
import Partner from '@app/api/partner/partner.actions';
import { PageSection, Title, CardTitle, CardFooter, Checkbox, Chip, ChipGroup, TextInput, Form, FormGroup } from '@patternfly/react-core';
import { CardBody, Button, Card, CardHeader, Col, Row } from 'reactstrap';
import { connect } from 'react-redux';
import { State } from '@app/store/root-reducer';
import { actionCreators as sessionActionCreators } from '@app/common/session/session.reducer';
import UserSession from '@app/common/user-session';

interface SettingsState {
  carrier: string;
  carriers: string[];
  message: string;
  messageType: string;
  linkTypes: { name: string; checked: boolean }[];
  isDirty: boolean;
  initialCarriers: string[];
  initialLinkTypes: { name: string; checked: boolean }[];
}

interface Props {
  partnerId: string;
  notificationEnqueue: any;
}

class Settings extends React.Component<Props, SettingsState> {
  constructor(props) {
    super(props);

    this.state = {
      carrier: "",  
      carriers: [],
      message: "",
      messageType: "",
      linkTypes: [
        { name: 'Fiber', checked: false },
        { name: 'Cable', checked: false },
        { name: 'LTE', checked: false },
        { name: 'Satellite', checked: false },
        { name: 'Line-of-Sight', checked: false },
        { name: 'DSL', checked: false },
        { name: 'xDSL', checked: false },
        { name: '3G', checked: false },
        { name: '4G', checked: false },
        { name: '5G', checked: false },
        { name: 'Other', checked: false }
      ],
      isDirty: false,
      initialCarriers: [],
      initialLinkTypes: [],
    }
  }

  componentDidMount = async () => {
    this.loadCarriers();
    this.loadLinkTypes();
  }

  loadCarriers = async () => {
    try {
      let result = await Partner.getCarriers();
      const carriers = result.map(carrier => carrier.name);
      this.setState(() => ({
        carriers: carriers,
        initialCarriers: carriers
      }));
    }
    catch (error) {
      this.setState(() => ({
        message: "There was an error getting the carriers"
      }));
    }
  }

  loadLinkTypes = async () => {
    try {
      let result = await Partner.getLinkTypes();
      const linkTypeNames = new Set(result.map(linkType => linkType.name));
      const { linkTypes } = this.state;
      const allLinkTypes = linkTypes.map(linkType => {
        if (linkTypeNames.has(linkType.name)) {
          return { ...linkType, checked: true }
        }
        else {
          return linkType
        }
      });
      this.setState(() => ({
        linkTypes: allLinkTypes,
        initialLinkTypes: allLinkTypes
      }));
    }
    catch (error) {
      this.setState(() => ({
        message: "There was an error getting the link types"
      }));
    }
  }

  handleLinkTypeChange = (checked, event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    const { linkTypes } = this.state;
    const updatedLinkTypes = linkTypes.map(linkType => 
      linkType.name === name ? {...linkType, checked: value} : linkType
    );
    this.setState(() => ({ 
      linkTypes: updatedLinkTypes,
      isDirty: true
    }));
  };

  deleteCarrier = (carrier) => {
    const { carriers } = this.state;
    const index = carriers.indexOf(carrier);
    if (index !== -1) {
      carriers.splice(index, 1);
      this.setState(() => ({ 
        carriers: carriers,
        isDirty: true
      }));
    }
  }

  addCarrier = () => {
    const { carrier, carriers } = this.state;
    if (carrier.length > 0) {
      carriers.push(carrier);
      this.setState(() => ({
        carrier: "",  
        carriers: carriers,
        isDirty: true
      }));
    }
  }

  handleCarrier = (value) => {
    this.setState(() => ({ 
      carrier: value 
    }));
  }

  handleAddCarrier = (event) => {
    if (event.charCode == 13) {
      this.addCarrier();
    }
  }

  handleSubmit = async () => {
    try {
      const checkedLinkTypes = this.state.linkTypes.filter(linkType => linkType.checked === true).map(linkType => linkType.name);
      
      // Check what has changed
      const carriersChanged = JSON.stringify([...this.state.carriers].sort()) !== JSON.stringify([...this.state.initialCarriers].sort());
      const linkTypesChanged = JSON.stringify([...this.state.linkTypes].sort((a, b) => a.name.localeCompare(b.name))) !== JSON.stringify([...this.state.initialLinkTypes].sort((a, b) => a.name.localeCompare(b.name)));
      
      // Update carriers and link types
      const result = await Partner.updateSettings(this.state.carriers, checkedLinkTypes);

      this.setState(() => ({
        message: "Settings updated successfully!",
        isDirty: false,
        initialCarriers: [...this.state.carriers],
        initialLinkTypes: [...this.state.linkTypes]
      }));

      // Show specific notifications based on what changed
      if (carriersChanged && linkTypesChanged) {
        this.props.notificationEnqueue({
          message: 'Carriers Updated',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        });
        this.props.notificationEnqueue({
          message: 'Link Types Updated',
          options: {
            key: new Date().getTime() + Math.random() + 1,
            variant: 'success',
          },
        });
      } else if (carriersChanged) {
        this.props.notificationEnqueue({
          message: 'Carriers Updated',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        });
      } else if (linkTypesChanged) {
        this.props.notificationEnqueue({
          message: 'Link Types Updated',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        });
      }
    }
    catch (error) {
      console.log(error)
      this.setState(() => ({
        message: "There was an error updating the settings, or you are not permitted to perform this action"
      }));
    }
  }

  render() {
    const { linkTypes, carriers, carrier } = this.state;
    const { partnerId } = this.props;
    const linkTypesComponent = linkTypes.map((linkType, index) => (
      <Checkbox 
        label={linkType.name} 
        name={linkType.name} 
        aria-label={linkType.name} 
        id={`link-type-${index}`} 
        key={`link-type-${index}`} 
        isChecked={linkType.checked} 
        onChange={(checked, event) => this.handleLinkTypeChange(checked, event)}
        className="me-2 mb-2"
      />
    ));
    const carrierComponent = carriers.map(carrier => (
      <Chip key={carrier} onClick={() => this.deleteCarrier(carrier)}>
        {carrier}
      </Chip>
    ));

    return (
      <Col lg={12} md={12} sm={12} xs={12} className="mx-0 px-0">
        <CardBody className="settings-card-body">
          <Row>
            <Col lg={3} md={3} sm={12} xs={12}>
              <Card className="h-100">
                <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                  <div className="w-100 d-flex justify-content-between">
                    <div className="icon-heading-container">
                      <div className="icon-heading">
                      Link Types             
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="d-flex flex-wrap align-content-start">
                  {linkTypesComponent}
                </CardBody>
              </Card>
            </Col>
            <Col lg={9} md={9} sm={12} xs={12}>
              <Card>
                <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                  <div className="w-100 d-flex justify-content-between">
                    <div className="icon-heading-container">
                      <div className="icon-heading">
                        Carriers
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <p className="font-12">Add the ISPs that you will use to deploy last-mile connections. This list will populate a drop-down menu in the site configuration page. We use this information to help you understand your deployment environment and better report on performance.</p>
                  <div className="d-flex mb-3">
                    <FormGroup className="form-item">
                      <TextInput
                        type="text" id="carrier" name="carrier" autoComplete="new-password"
                        value={carrier} onChange={this.handleCarrier} onKeyPress={this.handleAddCarrier} className="form-control"
                      />
                    </FormGroup>
                    <Button className="ethica-button-green ms-2" size="sm me-2" onClick={this.addCarrier}>Add</Button>
                  </div>
                  <ChipGroup categoryName="Carriers:" numChips={10}>
                      {carriers.length > 0 ? carrierComponent : <div>No carriers specified</div>}
                  </ChipGroup>
                </CardBody>
              </Card>                
            </Col>
          </Row>
        </CardBody>
      </Col>
    );
  }
}

// Create a ConnectedSettings component with proper ref forwarding
const ConnectedSettings = connect(
  (state: State) => {
    // Check if user is impersonating a partner space
    const isImpersonating = UserSession.getParam('isImpersonating');
    
    // If impersonating, get partnerId from UserSession (localStorage)
    // Otherwise, get it from Redux store
    const partnerId = isImpersonating 
      ? UserSession.getPartnerId() 
      : state.common.auth.session?.partnerId;
    
    return {
      partnerId,
    };
  },
  {
    notificationEnqueue: sessionActionCreators.notificationEnqueue
  },
  null,
  { forwardRef: true }
)(Settings);

// Create the final component with forwardRef that exposes handleSubmit and isDirty
const SettingsWithRef = React.forwardRef<{ handleSubmit: () => Promise<void>; isDirty: () => boolean }, {}>((props, ref) => {
  const settingsRef = React.useRef<Settings>(null);
  
  React.useImperativeHandle(ref, () => ({
    handleSubmit: async () => {
      if (settingsRef.current?.handleSubmit) {
        return await settingsRef.current.handleSubmit();
      }
    },
    isDirty: () => settingsRef.current?.state.isDirty || false
  }));
  
  return <ConnectedSettings {...props} ref={settingsRef} />;
});

export default SettingsWithRef;
